import React from 'react'
import { FileText, Mic } from 'lucide-react'
import { cn } from 'lib/utils'
import type { IAttachment } from 'features/Chat/Chat.types'

/**
 * Inline media on a message: images, video, and a chip for anything else.
 *
 * Lifted out of ChatMessage so the popup's bubbles and the existing
 * project/portfolio message list render attachments the same way, from one
 * implementation.
 */
const MessageAttachments = ({
	attachments = [],
	className = '',
}: {
	attachments?: IAttachment[]
	className?: string
}) => {
	if (!attachments || attachments.length === 0) return null

	return (
		<div className={cn('mt-1 flex flex-wrap gap-2', className)}>
			{attachments.map((att, i) => {
				if (att.kind === 'image') {
					return (
						<a key={i} href={att.url} target="_blank" rel="noreferrer" className="block">
							<img
								src={att.url}
								alt={att.name || 'Attachment'}
								loading="lazy"
								className="max-h-48 max-w-[240px] rounded-md border border-gray-200 object-cover"
							/>
						</a>
					)
				}
				if (att.kind === 'video') {
					return (
						<video
							key={i}
							src={att.url}
							controls
							playsInline
							preload="metadata"
							className="max-h-48 max-w-[240px] rounded-md border border-gray-200"
						/>
					)
				}
				if (att.kind === 'audio') {
					return (
						<div
							key={i}
							className="flex w-full max-w-[280px] items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"
						>
							<Mic className="h-4 w-4 shrink-0 text-gray-400" />
							{/* The native player, deliberately: it gives scrubbing, speed and
							    the lock-screen controls for free, and on iOS it is the only
							    thing that reliably plays back audio/mp4. `metadata` so a
							    thread of voice notes does not download them all on open. */}
							<audio src={att.url} controls preload="metadata" className="h-8 w-full" />
						</div>
					)
				}
				return (
					<a
						key={i}
						href={att.url}
						target="_blank"
						rel="noreferrer"
						className="inline-flex max-w-[240px] items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 no-underline transition-colors hover:bg-gray-100"
					>
						<FileText className="h-4 w-4 shrink-0 text-gray-400" />
						<span className="truncate">{att.name || 'Attachment'}</span>
					</a>
				)
			})}
		</div>
	)
}

export default MessageAttachments
